import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useBehaviourSummary, useBehaviourIncidents, useSyncBehaviour } from '../api/behaviour';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export default function BehaviourPage() {
  const queryClient = useQueryClient();
  const { data: response, isLoading } = useBehaviourSummary();
  const [selectedWeekOffset, setSelectedWeekOffset] = useState(0); // 0 = this week, -1 = last week, etc.
  const syncMutation = useSyncBehaviour();

  // Pull-to-refresh state
  const [pullDistance, setPullDistance] = useState(0);
  const [startY, setStartY] = useState(0);
  const [isPulling, setIsPulling] = useState(false);

  // Calculate the start date for the selected week
  const getWeekStart = (offset: number = 0): Date => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Adjust to Monday
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() + diff + (offset * 7));
    weekStart.setHours(0, 0, 0, 0);
    return weekStart;
  };

  const selectedWeekStart = getWeekStart(selectedWeekOffset);
  const selectedWeekEnd = new Date(selectedWeekStart);
  selectedWeekEnd.setDate(selectedWeekEnd.getDate() + 6); // Sunday

  // Fetch incidents for both children for the selected week
  const { data: alexIncidents } = useBehaviourIncidents('Alex', 'week', selectedWeekStart);
  const { data: thomasIncidents } = useBehaviourIncidents('Thomas', 'week', selectedWeekStart);

  // Poll for updates while sync is running
  useEffect(() => {
    if (response?.meta?.lastSyncStatus === 'running') {
      const interval = setInterval(() => {
        queryClient.invalidateQueries({ queryKey: ['behaviour', 'summary'] });
      }, 2000); // Poll every 2 seconds

      return () => clearInterval(interval);
    }
  }, [response?.meta?.lastSyncStatus, queryClient]);

  const formatLastSync = (dateString: string | null) => {
    if (!dateString) return 'never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  const formatIncidentDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-GB', { month: 'short' });
    const suffix = ['th', 'st', 'nd', 'rd'][((day % 100) - 20) % 10] || ['th', 'st', 'nd', 'rd'][day % 100] || 'th';
    return `${day}${suffix} ${month}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading behaviour data...</div>
      </div>
    );
  }

  const summary = Array.isArray(response?.data) ? response.data : [];
  const meta = response?.meta;

  // Calculate chart data from the selected week's incidents
  const calculateWeekTotals = () => {
    const alexPoints = alexIncidents?.filter(i => i.incident_type === 'positive')
      .reduce((sum, i) => sum + (i.points || 0), 0) || 0;
    const alexNegatives = alexIncidents?.filter(i => i.incident_type === 'negative').length || 0;
    const thomasPoints = thomasIncidents?.filter(i => i.incident_type === 'positive')
      .reduce((sum, i) => sum + (i.points || 0), 0) || 0;
    const thomasNegatives = thomasIncidents?.filter(i => i.incident_type === 'negative').length || 0;

    return [
      { type: 'Positives', Alex: alexPoints, Thomas: thomasPoints },
      { type: 'Negatives', Alex: alexNegatives, Thomas: thomasNegatives },
    ];
  };

  const chartData = calculateWeekTotals();

  const formatWeekRange = () => {
    const day = selectedWeekStart.getDate();
    const month = selectedWeekStart.toLocaleDateString('en-GB', { month: 'short' });
    return `${day} ${month}`;
  };

  const isCurrentWeek = selectedWeekOffset === 0;

  const handleSync = async () => {
    try {
      await syncMutation.mutateAsync();
      // Immediately refetch to show 'running' status
      queryClient.invalidateQueries({ queryKey: ['behaviour', 'summary'] });
    } catch (error) {
      console.error('Failed to sync behaviour data:', error);
    }
  };

  // Pull-to-refresh handlers (supports both touch and mouse)
  const handlePullStart = (clientY: number) => {
    if (window.scrollY === 0) {
      setStartY(clientY);
      setIsPulling(true);
    }
  };

  const handlePullMove = (clientY: number) => {
    if (!isPulling || window.scrollY > 0) {
      setIsPulling(false);
      setPullDistance(0);
      return;
    }

    const distance = clientY - startY;

    if (distance > 0) {
      setPullDistance(Math.min(distance, 120)); // Cap at 120px
    }
  };

  const handlePullEnd = () => {
    if (pullDistance > 80 && !syncMutation.isPending) {
      handleSync();
    }
    setIsPulling(false);
    setPullDistance(0);
    setStartY(0);
  };

  // Touch event handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches[0]) {
      handlePullStart(e.touches[0].clientY);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches[0]) {
      handlePullMove(e.touches[0].clientY);
    }
  };

  const handleTouchEnd = () => {
    handlePullEnd();
  };

  // Mouse event handlers (for desktop testing)
  const handleMouseDown = (e: React.MouseEvent) => {
    handlePullStart(e.clientY);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isPulling) {
      handlePullMove(e.clientY);
    }
  };

  const handleMouseUp = () => {
    if (isPulling) {
      handlePullEnd();
    }
  };

  return (
    <div
      className="container mx-auto p-4 sm:p-6 max-w-7xl"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Pull-to-refresh indicator */}
      <div
        className="fixed top-16 left-0 right-0 flex justify-center transition-opacity"
        style={{
          opacity: pullDistance > 0 ? Math.min(pullDistance / 80, 1) : 0,
          transform: `translateY(${Math.min(pullDistance - 80, 0)}px)`,
        }}
      >
        <div className="bg-white rounded-full p-3 shadow-lg">
          <svg
            className={`h-6 w-6 text-blue-600 ${pullDistance > 80 ? 'animate-spin' : ''}`}
            style={{ transform: `rotate(${pullDistance * 3}deg)` }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </div>
      </div>

      {/* Desktop-only sync button and last synced info */}
      <div className="hidden sm:flex justify-between items-center mb-4">
          <div>
            {meta?.lastSyncedAt && (
              <p className="text-xs text-gray-500">
                Last synced {formatLastSync(meta.lastSyncedAt)}
              </p>
            )}
          </div>
        <button
          onClick={handleSync}
          disabled={syncMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          title="Refresh behaviour data"
        >
          {syncMutation.isPending ? (
            <>
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Syncing...</span>
            </>
          ) : (
            <>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Sync Now</span>
            </>
          )}
        </button>
      </div>

      {/* Sync in progress banner */}
      {meta?.lastSyncStatus === 'running' && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <div>
            <p className="text-blue-900 font-medium">Sync in progress</p>
            <p className="text-blue-700 text-sm">Fetching latest behaviour data from Arbor...</p>
          </div>
        </div>
      )}

      {/* Error banner */}
      {meta?.lastSyncStatus === 'error' && meta.lastSyncError && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800 text-sm">
            <span className="font-semibold">Last sync had errors:</span> {meta.lastSyncError}
          </p>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
        {summary.map(child => (
          <div key={child.name} className="bg-white p-4 sm:p-6 rounded-lg shadow-md border border-gray-200">
            <h2 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 text-gray-800">{child.name}</h2>
            <div className="space-y-3">
              <div className="border-b pb-2">
                <p className="text-sm font-medium text-gray-600">This Week</p>
                <p className="text-lg">
                  <span className="text-green-600 font-semibold">{child.points_this_week} points</span>
                  {', '}
                  <span className="text-red-600 font-semibold">{child.negatives_this_week} negatives</span>
                </p>
              </div>
              <div className="border-b pb-2">
                <p className="text-xs font-medium text-gray-500">This Term</p>
                <p className="text-sm">
                  <span className="text-green-600 font-semibold">{child.points_term} points</span>
                  {', '}
                  <span className="text-red-600 font-semibold">{child.negatives_term} negatives</span>
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">This School Year</p>
                <p className="text-sm">
                  <span className="text-green-600 font-semibold">{child.points_year} points</span>
                  {', '}
                  <span className="text-red-600 font-semibold">{child.negatives_year} negatives</span>
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Week Navigation and Chart */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md border border-gray-200 mb-6 sm:mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-base sm:text-xl font-semibold text-gray-800">
            {isCurrentWeek ? 'This Week' : `Week of ${formatWeekRange()}`}
          </h2>
          <div className="flex gap-1 sm:gap-2">
            <button
              onClick={() => setSelectedWeekOffset(selectedWeekOffset - 1)}
              className="p-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              title="Previous week"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => setSelectedWeekOffset(0)}
              disabled={isCurrentWeek}
              className="p-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
              title="Current week"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>
            <button
              onClick={() => setSelectedWeekOffset(selectedWeekOffset + 1)}
              disabled={selectedWeekOffset >= 0}
              className="p-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
              title="Next week"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="type" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="Alex" fill="#3b82f6" />
            <Bar dataKey="Thomas" fill="#10b981" />
          </BarChart>
        </ResponsiveContainer>

        {/* Alex's Incidents */}
        <div className="mt-6 sm:mt-8">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3">Alex</h3>
          {alexIncidents && alexIncidents.length > 0 ? (
            <div className="space-y-2">
              {alexIncidents.map((inc, i) => (
                <div
                  key={i}
                  className={`p-3 rounded-lg border ${
                    inc.incident_type === 'positive'
                      ? 'bg-green-50 border-green-200'
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="grid gap-x-4 gap-y-2" style={{ gridTemplateColumns: '1fr 3fr' }}>
                    <div className="text-sm font-medium text-gray-700">
                      {formatIncidentDate(inc.incident_date)}
                    </div>
                    <div className="text-sm text-gray-700">
                      {inc.category || inc.behaviour || '-'}
                    </div>
                    {inc.incident_type === 'positive' && (
                      <>
                        <div className="text-sm font-semibold text-green-700">
                          +{inc.points} points
                        </div>
                        <div className="text-sm text-gray-600">
                          {inc.event || '-'}
                        </div>
                      </>
                    )}
                    {inc.incident_type === 'negative' && (
                      <>
                        <div></div>
                        <div className="text-sm text-gray-600">
                          {inc.event || '-'}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm py-4">No incidents this week</p>
          )}
        </div>

        {/* Thomas's Incidents */}
        <div className="mt-6 sm:mt-8">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3">Thomas</h3>
          {thomasIncidents && thomasIncidents.length > 0 ? (
            <div className="space-y-2">
              {thomasIncidents.map((inc, i) => (
                <div
                  key={i}
                  className={`p-3 rounded-lg border ${
                    inc.incident_type === 'positive'
                      ? 'bg-green-50 border-green-200'
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="grid gap-x-4 gap-y-2" style={{ gridTemplateColumns: '1fr 3fr' }}>
                    <div className="text-sm font-medium text-gray-700">
                      {formatIncidentDate(inc.incident_date)}
                    </div>
                    <div className="text-sm text-gray-700">
                      {inc.category || inc.behaviour || '-'}
                    </div>
                    {inc.incident_type === 'positive' && (
                      <>
                        <div className="text-sm font-semibold text-green-700">
                          +{inc.points} points
                        </div>
                        <div className="text-sm text-gray-600">
                          {inc.event || '-'}
                        </div>
                      </>
                    )}
                    {inc.incident_type === 'negative' && (
                      <>
                        <div></div>
                        <div className="text-sm text-gray-600">
                          {inc.event || '-'}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm py-4">No incidents this week</p>
          )}
        </div>
      </div>
    </div>
  );
}
